import React, { useEffect, useRef, useState } from "react";
import { FaCog } from "react-icons/fa";
import { FiChevronDown, FiFileText, FiPlus, FiUploadCloud } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS,
  UNCATEGORIZED_PAGE_SIZE,
  UPLOAD_ACCEPT,
  categoryLabel,
  formatFileSize,
  isLockedCategory,
  validateUploadFile,
} from "../helpers/documentCategories";
import { formatDisplayDate } from "../helpers/partyDetailsHelper";
import { useDocumentPreview } from "./DocumentViewer";

export const UNCATEGORIZED_KEY = "UNCATEGORIZED";

// Extra "Atenção" banner line listing missing important categories; the button jumps to the first one.
// Off for now (TDDS-3492): important documents are advisory, not required.
export const buildMissingDocumentsAlert = (missing, t, onGo, enabled = PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS) => {
  if (!enabled || !Array.isArray(missing) || missing.length === 0) return [];
  return [{
    id: "missing-documents",
    level: "MEDIUM",
    content: (
      <span>
        {t("documents.missingAlert").replace("{list}", missing.map((key) => categoryLabel(key, t)).join(", "))}
        {" "}
        <button type="button" className="party-alert-action" onClick={() => onGo(missing[0])}>
          {t("documents.goToMissing")}
        </button>
      </span>
    ),
  }];
};

// Friendly messages for the backend's PATCH /files/{id}/category and POST /files errors.
export const documentErrorMessage = (error, t, fallbackKey) => {
  if (error?.status === 400) return t("documents.errors.invalidCategory");
  if (error?.status === 403) return t("documents.errors.forbidden");
  if (error?.status === 404) return t("documents.errors.notFound");
  if (error?.status === 409 || error?.code === "CATEGORY_LOCKED") return t("documents.errors.locked");
  return t(fallbackKey);
};

const isMobileViewport = () =>
  typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(max-width: 680px)").matches;

const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");

// Drop target with an enter/leave depth counter so nested children don't flicker the highlight.
const DropZone = ({ enabled, onFiles, className, hint, testId, children }) => {
  const depth = useRef(0);
  const [active, setActive] = useState(false);
  const dragging = enabled && active;

  const handlers = enabled ? {
    onDragEnter: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      depth.current += 1;
      setActive(true);
    },
    onDragOver: (event) => {
      if (hasDraggedFiles(event)) event.preventDefault();
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setActive(false);
    },
    onDrop: (event) => {
      event.preventDefault();
      depth.current = 0;
      setActive(false);
      const files = Array.from(event.dataTransfer?.files || []);
      if (files.length) onFiles(files);
    },
  } : {};

  // Same element structure whether enabled or not, so children never remount.
  return (
    <div
      className={`${className}${enabled ? " docs-dropzone" : ""}${dragging ? " is-drag-active" : ""}`}
      data-testid={testId}
      {...handlers}
    >
      {children}
      {dragging && <div className="docs-drop-hint" aria-hidden="true">{hint}</div>}
    </div>
  );
};


// Portuguese/English names for Docrio tags shown as their own group; unknown tags keep the tag text.
export const tagGroupLabel = (group, t) => {
  const key = `documents.tagLabels.${group.label}`;
  const translated = t(key);
  return translated && translated !== key ? translated : group.label;
};

const CategoryPicker = ({ id, value, onChange, t, categories, placeholder, label }) => (
  <select
    id={id}
    className={`docs-select${value ? "" : " is-placeholder"}`}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    aria-label={label}
  >
    <option value="">{placeholder}</option>
    {categories.map((key) => (
      <option key={key} value={key}>{categoryLabel(key, t)}</option>
    ))}
  </select>
);

const DocumentRow = ({ doc, t, canEdit, onView, viewing, onReclassify, idPrefix, categories, needsClassification }) => {
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  const pickerId = `${idPrefix}-picker-${doc.id}`;
  const locked = isLockedCategory(doc.category);
  const showPicker = canEdit && !locked && (needsClassification || editing);
  const meta = [
    doc.createdDate || doc.lastModifiedDate ? formatDisplayDate(doc.createdDate || doc.lastModifiedDate) : "",
    formatFileSize(doc.fileSize),
  ].filter(Boolean).join(" · ");

  const save = async () => {
    setSaving(true);
    const ok = await onReclassify(doc, choice);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <li className="docs-row" data-testid={`docs-row-${doc.id}`}>
      <div className="docs-row-main">
        <FiFileText className="docs-row-icon" aria-hidden="true" />
        <div className="docs-row-text">
          <span className="docs-row-name" title={doc.name}>{doc.name}</span>
          {meta && <span className="docs-row-meta">{meta}</span>}
        </div>
        <div className="docs-row-actions">
          <button
            type="button"
            className="docs-link-button"
            onClick={() => onView(doc)}
            disabled={viewing}
            aria-label={`${t("documents.view")}: ${doc.name}`}
          >
            {viewing && <FaCog className="spinner-cog" aria-hidden="true" />}
            {t("documents.view")}
          </button>
          {canEdit && !locked && !needsClassification && !editing && (
            <button
              type="button"
              className="docs-link-button"
              onClick={() => {
                setChoice("");
                setEditing(true);
              }}
              aria-label={`${t("documents.changeCategory")}: ${doc.name}`}
            >
              {t("documents.changeCategory")}
            </button>
          )}
        </div>
      </div>
      {showPicker && (
        <div className="docs-row-picker">
          <label htmlFor={pickerId} className="docs-picker-label">{t("documents.categoryLabel")}</label>
          <CategoryPicker
            id={pickerId}
            value={choice}
            onChange={setChoice}
            t={t}
            categories={categories}
            placeholder={t("documents.selectCategory")}
          />
          <button
            type="button"
            className="docs-button docs-button-primary"
            onClick={save}
            disabled={saving || !choice || choice === doc.category}
          >
            {saving ? t("documents.saving") : t("documents.save")}
          </button>
          {editing && (
            <button type="button" className="docs-button" onClick={() => setEditing(false)} disabled={saving}>
              {t("documents.cancel")}
            </button>
          )}
        </div>
      )}
    </li>
  );
};

// The title area toggles the card; badges and the upload button stay outside the toggle.
const SectionHeader = ({ id, title, open, onToggle, children }) => (
  <div id={id} className="docs-section-header">
    <button
      type="button"
      className="docs-section-toggle"
      aria-expanded={open}
      aria-controls={`${id}-body`}
      onClick={onToggle}
    >
      <FiChevronDown className={`docs-chevron${open ? " is-open" : ""}`} aria-hidden="true" />
      <span className="docs-section-title">{title}</span>
    </button>
    <span className="docs-section-meta">{children}</span>
  </div>
);

const safeId = (key) => String(key).replace(/[^A-Za-z0-9_-]/g, "_");

/**
 * Documents grouped by category (TDDS-3384), laid out like the BHP client portal: an always-open
 * "Documentos a categorizar" block above "Documentos classificados" (one card per category the backend
 * returned, then one per Docrio tag group). `data` is the output of normalizeGroupedFiles.
 * Edit controls only render when `canEdit`; `uploadsDisabled` (terminated matter) hides uploads but
 * keeps reclassification. With `fetchViewUrl` (doc => signed URL), PDFs, images and files of unknown
 * type open in an in-page viewer; Word/Excel files (or without it) go through `onView` (download).
 */
const CategorizedDocuments = ({
  data,
  canEdit,
  uploadsDisabled = false,
  onView,
  viewingId,
  fetchViewUrl,
  onUpload,
  onReclassify,
  focusRequest,
  idPrefix = "docs",
}) => {
  const { t } = useTranslation();
  const [mobile] = useState(isMobileViewport);
  const [openOverrides, setOpenOverrides] = useState({});
  const [visibleUncategorized, setVisibleUncategorized] = useState(UNCATEGORIZED_PAGE_SIZE);
  const [uploadType, setUploadType] = useState("");
  const [uploadingTarget, setUploadingTarget] = useState(null);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);
  const pendingCategory = useRef("");
  const pendingTarget = useRef("");

  const categories = data.categories || [];
  const tagGroups = data.tagGroups || [];
  const categoryKeys = categories.map((group) => group.key);
  const assignableKeys = categories.filter((group) => group.assignable !== false).map((group) => group.key);
  const canUpload = canEdit && !uploadsDisabled && typeof onUpload === "function";
  const sectionId = (key) => `${idPrefix}-section-${safeId(key)}`;

  const needsAction = (group) => Boolean(group.mandatory) && group.documents.length === 0;
  const isOpen = (key, defaultOpen) => (key in openOverrides ? openOverrides[key] : defaultOpen);
  const setOpen = (key, value) => setOpenOverrides((current) => ({ ...current, [key]: value }));
  // Expand/collapse applies to the classified block only; "a categorizar" is always open (TDDS-3486).
  const setAll = (value) => setOpenOverrides(
    Object.fromEntries([...categoryKeys, ...tagGroups.map((group) => group.key)].map((key) => [key, value])),
  );

  useEffect(() => {
    if (!focusRequest?.key) return;
    setOpenOverrides((current) => ({ ...current, [focusRequest.key]: true }));
    const element = document.getElementById(sectionId(focusRequest.key));
    if (element) {
      if (typeof element.scrollIntoView === "function") element.scrollIntoView({ behavior: "smooth", block: "start" });
      element.querySelector(".docs-section-toggle")?.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  const important = categories.filter((group) => group.mandatory);
  const importantSent = important.filter((group) => group.documents.length > 0).length;
  const totalDocuments = data.uncategorized.length
    + categories.reduce((count, group) => count + group.documents.length, 0)
    + tagGroups.reduce((count, group) => count + group.documents.length, 0);

  const uploadFiles = async (files, category, target) => {
    setMessage(null);
    const errors = [];
    const valid = [];
    files.forEach((file) => {
      const error = validateUploadFile(file, t);
      if (error) errors.push(error);
      else valid.push(file);
    });
    if (!valid.length) {
      setMessage({ type: "error", text: errors.join(" ") });
      return;
    }

    setUploadingTarget(target);
    let uploaded = 0;
    for (const file of valid) {
      try {
        // Sequential on purpose: keeps the signed-URL flow and the refresh simple.
        // eslint-disable-next-line no-await-in-loop
        await onUpload(file, category || null);
        uploaded += 1;
      } catch (error) {
        console.error("Document upload failed:", error);
        const reason = error?.status === 400 || error?.status === 403
          ? ` ${documentErrorMessage(error, t, "documents.reclassifyError")}`
          : "";
        errors.push(`${t("documents.uploadError").replace("{name}", file.name)}${reason}`);
      }
    }
    setUploadingTarget(null);
    if (target === "generic") setUploadType("");
    if (category) setOpen(category, true);

    const success = !uploaded ? "" : category
      ? t("documents.uploadSuccess")
        .replace("{count}", String(uploaded))
        .replace("{category}", categoryLabel(category, t))
      : t("documents.uploadSuccessUncategorized").replace("{count}", String(uploaded));
    setMessage({ type: errors.length ? "error" : "success", text: [success, ...errors].filter(Boolean).join(" ") });
  };

  const pickFiles = (category, target) => {
    pendingCategory.current = category || "";
    pendingTarget.current = target || category || "generic";
    fileInputRef.current?.click();
  };

  const reclassify = async (doc, category) => {
    setMessage(null);
    try {
      await onReclassify(doc, category);
      setOpen(category, true);
      setMessage({
        type: "success",
        text: t("documents.reclassifySuccess")
          .replace("{name}", doc.name)
          .replace("{category}", categoryLabel(category, t)),
      });
      return true;
    } catch (error) {
      setMessage({ type: "error", text: documentErrorMessage(error, t, "documents.reclassifyError") });
      return false;
    }
  };

  // PDFs/images open in the in-page viewer (TDDS-3494); the viewer shows progress while the signed URL loads.
  const { open: openPreview, previewingId, viewerElement } = useDocumentPreview({
    fetchViewUrl,
    onDownload: onView,
    onError: (key) => setMessage({ type: "error", text: t(key) }),
  });
  const view = (doc) => {
    setMessage(null);
    return openPreview(doc);
  };

  const renderRows = (docs, needsClassification = false) => (
    <ul className="docs-list">
      {docs.map((doc) => (
        <DocumentRow
          key={doc.id}
          doc={doc}
          t={t}
          canEdit={canEdit}
          onView={view}
          viewing={viewingId === doc.id || previewingId === doc.id}
          onReclassify={reclassify}
          idPrefix={idPrefix}
          categories={assignableKeys}
          needsClassification={needsClassification}
        />
      ))}
    </ul>
  );

  const emptyText = (group) => {
    if (group.key === "OTHER") return t("documents.emptyOther");
    if (group.assignable === false) return t("documents.emptyAutomatic");
    if (group.mandatory) return canUpload ? t("documents.emptyImportant") : t("documents.emptyExpectedReadOnly");
    return canUpload ? t("documents.emptyExpected") : t("documents.emptyExpectedReadOnly");
  };

  const hiddenUncategorized = Math.max(0, data.uncategorized.length - visibleUncategorized);
  const busy = uploadingTarget !== null;

  const renderCategory = (group) => {
    const empty = group.documents.length === 0;
    const uploadable = canUpload && group.assignable !== false;
    const open = isOpen(group.key, needsAction(group) || !mobile);
    const id = sectionId(group.key);
    const label = categoryLabel(group.key, t);
    // Important documents are advisory (TDDS-3492): a missing one looks like any other missing document.
    const notReceived = empty && (group.mandatory || (group.key !== "OTHER" && group.expected !== false));
    return (
      <DropZone
        key={group.key}
        enabled={uploadable && !busy}
        onFiles={(files) => uploadFiles(files, group.key, group.key)}
        className={`docs-section${empty ? " is-empty" : ""}${open ? "" : " is-collapsed"}`}
        hint={t("documents.upload.dropInto").replace("{category}", label)}
        testId={`docs-section-${group.key}`}
      >
        <SectionHeader id={id} title={label} open={open} onToggle={() => setOpen(group.key, !open)}>
          {group.mandatory && <span className="docs-badge is-important">{t("documents.important")}</span>}
          {empty
            ? notReceived && <span className="docs-badge is-missing">{t("documents.notReceived")}</span>
            : <span className="docs-count">{group.documents.length}</span>}
          {uploadable && (
            <button
              type="button"
              className={`docs-add-button${empty ? " is-primary" : ""}`}
              onClick={() => pickFiles(group.key)}
              disabled={busy}
              aria-label={empty
                ? t("documents.sendTo").replace("{category}", label)
                : t("documents.addTo").replace("{category}", label)}
            >
              {uploadingTarget === group.key
                ? <FaCog className="spinner-cog" aria-hidden="true" />
                : <FiPlus aria-hidden="true" />}
              <span className="docs-add-label">{empty ? t("documents.send") : t("documents.add")}</span>
            </button>
          )}
        </SectionHeader>
        {open && (
          <div id={`${id}-body`} className="docs-section-body">
            {empty ? <p className="docs-empty">{emptyText(group)}</p> : renderRows(group.documents)}
          </div>
        )}
      </DropZone>
    );
  };

  const renderTagGroup = (group) => {
    const open = isOpen(group.key, !mobile);
    const id = sectionId(group.key);
    return (
      <div key={group.key} className={`docs-section${open ? "" : " is-collapsed"}`} data-testid={`docs-section-${group.key}`}>
        <SectionHeader id={id} title={tagGroupLabel(group, t)} open={open} onToggle={() => setOpen(group.key, !open)}>
          <span className="docs-count">{group.documents.length}</span>
        </SectionHeader>
        {open && <div id={`${id}-body`} className="docs-section-body">{renderRows(group.documents)}</div>}
      </div>
    );
  };

  return (
    <div className="docs-categorized" data-testid="categorized-documents">
      {viewerElement}

      {!canEdit && <p className="docs-readonly-note" data-testid="docs-readonly-note">{t("documents.readOnly")}</p>}
      {canEdit && uploadsDisabled && <p className="docs-readonly-note">{t("clientsTable.termination.uploadsDisabled")}</p>}

      {canEdit && totalDocuments > 0 && (
        <div className="docs-review-tip" role="note" data-testid="docs-review-tip">
          <strong>{t("documents.reviewTipTitle")}</strong> {t("documents.reviewTip")}
        </div>
      )}

      {message && (
        <div
          className={`status-message ${message.type === "error" ? "is-error" : "is-success"}`}
          role={message.type === "error" ? "alert" : "status"}
          data-testid="docs-message"
        >
          {message.text}
        </div>
      )}

      {important.length > 0 && (
        <p className="docs-important-summary" data-testid="docs-important-summary">
          {t("documents.importantSummary")
            .replace("{sent}", String(importantSent))
            .replace("{total}", String(important.length))}
        </p>
      )}

      {canUpload && (
        <DropZone
          enabled={!busy}
          onFiles={(files) => uploadFiles(files, uploadType, "generic")}
          className="docs-upload-panel"
          hint={t("documents.upload.dropActive")}
          testId="docs-generic-dropzone"
        >
          <FiUploadCloud className="docs-upload-icon" aria-hidden="true" />
          <div className="docs-upload-fields">
            <span className="docs-upload-title">{t("documents.upload.title")}</span>
            <p className="docs-upload-hint" data-testid="docs-upload-hint">{t("documents.upload.intro")}</p>
            <div className="docs-upload-controls">
              <CategoryPicker
                id={`${idPrefix}-upload-type`}
                value={uploadType}
                onChange={setUploadType}
                t={t}
                categories={assignableKeys}
                placeholder={t("documents.upload.unknownType")}
                label={t("documents.upload.typeLabel")}
              />
              <button
                type="button"
                className="docs-button docs-button-primary"
                onClick={() => pickFiles(uploadType, "generic")}
                disabled={busy}
              >
                {uploadingTarget === "generic" ? t("documents.uploading") : t("documents.upload.choose")}
              </button>
            </div>
          </div>
        </DropZone>
      )}

      {canUpload && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={UPLOAD_ACCEPT}
          className="docs-hidden-input"
          data-testid="docs-file-input"
          aria-hidden="true"
          tabIndex={-1}
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = "";
            if (files.length) uploadFiles(files, pendingCategory.current, pendingTarget.current || "generic");
          }}
        />
      )}

      {data.uncategorized.length > 0 && (
        <section
          className="docs-block docs-block-review"
          aria-labelledby={`${idPrefix}-heading-uncategorized`}
          data-testid="docs-section-UNCATEGORIZED"
          id={sectionId(UNCATEGORIZED_KEY)}
        >
          <div className="docs-block-header">
            <h4 className="docs-block-title" id={`${idPrefix}-heading-uncategorized`}>{t("documents.uncategorized")}</h4>
            <span className="docs-count is-review">{data.uncategorized.length}</span>
          </div>
          <p className="docs-block-intro">
            {canEdit ? t("documents.uncategorizedIntro") : t("documents.uncategorizedIntroReadOnly")}
          </p>
          {renderRows(data.uncategorized.slice(0, visibleUncategorized), true)}
          {hiddenUncategorized > 0 && (
            <button
              type="button"
              className="docs-button docs-show-more"
              onClick={() => setVisibleUncategorized((count) => count + UNCATEGORIZED_PAGE_SIZE)}
            >
              {t("documents.showMore")
                .replace("{count}", String(Math.min(UNCATEGORIZED_PAGE_SIZE, hiddenUncategorized)))
                .replace("{remaining}", String(hiddenUncategorized))}
            </button>
          )}
        </section>
      )}

      {(categories.length > 0 || tagGroups.length > 0) && (
        <section className="docs-block" aria-labelledby={`${idPrefix}-heading-categorized`}>
          <div className="docs-block-header">
            <h4 className="docs-block-title" id={`${idPrefix}-heading-categorized`}>{t("documents.categorized")}</h4>
          </div>
          <div className="docs-toolbar">
            <span className="docs-toolbar-label">{t("documents.categorizedToolbar")}</span>
            <button type="button" className="docs-link-button" onClick={() => setAll(true)}>
              {t("documents.expandAll")}
            </button>
            <span aria-hidden="true">·</span>
            <button type="button" className="docs-link-button" onClick={() => setAll(false)}>
              {t("documents.collapseAll")}
            </button>
          </div>
          <div className="docs-sections">
            {categories.map(renderCategory)}
            {tagGroups.map(renderTagGroup)}
          </div>
        </section>
      )}
    </div>
  );
};

export default CategorizedDocuments;
